import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { DataTable, TableBody } from '@/components/ui/data-table';
import { DataTableColumn } from '@/components/ui/data-table/DataTableContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, TrendingUp, Send } from 'lucide-react';
import { RefreshButton } from '@/components/ui/refresh-button';
import { useSubscriptionData, SubscriptionRecord } from '@/hooks/useSubscriptionData';
import EmptyState from '../EmptyState';
import { useStripeConnection } from '@/hooks/useStripeConnection';
import { useSubscriptionBulkUpgrade } from '@/hooks/useSubscriptionBulkUpgrade';
import { BulkUpgradePreviewModal } from './subscription/BulkUpgradePreviewModal';
import { useToast } from '@/hooks/use-toast';
import { canSelectSubscriptionForUpgrade } from '@/utils/subscriptionUpgradeHelpers';
import { usePagination } from '@/hooks/usePagination';
import { TableHeader, TableRow, TableHead } from '@/components/ui/table';
import { useModal } from '@/contexts/ModalContext';
import { useSubscriptionSavings } from '@/hooks/useSubscriptionSavings';
import { SubscriptionSavingsMetrics } from './subscription/SubscriptionSavingsMetrics';
import { SubscriptionMigrationInfo } from './subscription/SubscriptionMigrationInfo';

import { supabase } from '@/integrations/supabase/client';
import { fetchBusinessInfo } from '@/services/businesInfo';
import { STRIPE_PERCENT_FEE, STRIPE_FIXED_FEE } from '@/utils/fees/constants';

export interface DashboardSubscription extends SubscriptionRecord {
  product_id: string; // Alias for stripe_price_id
  customer_id?: string; // Add customer_id field
  customer_name?: string; // Customer name
  product_name?: string; // Product name from Stripe
  metadata?: Record<string, any>; // Add metadata field
  amount?: number; // Price amount in cents
  created?: number; // Creation timestamp in seconds
  new_price_id?: string; // The new price ID for upgraded subscriptions
  billing_interval?: string; // Billing period (month, year, etc.)
}

const getColumns = (activeCategory: string): DataTableColumn[] => {
  const base: DataTableColumn[] = [
    { id: 'select', label: '', align: 'left' },
    { id: 'customer', label: 'Customer', align: 'left' },
    { id: 'subscription_name', label: 'Subscription', align: 'left' },
  ];

  // Amount column label changes per tab
  if (activeCategory === 'upgraded') {
    base.push({ id: 'amount', label: 'New Total', align: 'left' });
  } else {
    base.push({ id: 'amount', label: 'Amount', align: 'left' });
  }

  // Fees for eligible & pending, Savings for switched to ACH & updated price
  if (activeCategory === 'regular' || activeCategory === 'pending') {
    base.push({ id: 'fees', label: 'Fees', align: 'left' });
  } else if (activeCategory === 'updated' || activeCategory === 'upgraded') {
    base.push({ id: 'savings', label: 'Savings', align: 'left' });
  }

  base.push({ id: 'billing_period', label: 'Period', align: 'left' });

  // Date column label changes per tab
  if (activeCategory === 'regular') {
    base.push({ id: 'date', label: 'Next Bill', align: 'left' });
  } else {
    base.push({ id: 'date', label: 'Updated On', align: 'left' });
  }

  return base;
};

const SubscriptionsTab: React.FC<{ stripeConnected: boolean }> = ({
  stripeConnected
}) => {
  const [activeCategory, setActiveCategory] = useState<'regular' | 'pending' | 'updated' | 'upgraded'>('regular');
  const columns = getColumns(activeCategory);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<Set<string>>(new Set());
  const [localStatusFilter, setLocalStatusFilter] = useState<string>('all');
  const [businessName, setBusinessName] = useState<string | undefined>();
  const [anchorBillingEnabled, setAnchorBillingEnabled] = useState(false);
  const {
    subscriptions,
    rawStripeData, // Get the raw Stripe data
    loading,
    error,
    products,
    getProduct,
    refreshSubscriptions,
  } = useSubscriptionData({ stripeConnected });

  const { isLoading } = useStripeConnection();
  const { toast } = useToast();
  const { openModal } = useModal();
  
  // Calculate savings metrics
  const savingsMetrics = useSubscriptionSavings(subscriptions);
  
  // Bulk upgrade functionality
  const {
    selectedSubscriptions: bulkSelectedSubscriptions,
    isPreviewModalOpen,
    isLoading: isBulkLoading,
    preview,
    error: bulkError,
    toggleSubscriptionSelection,
    selectAllSubscriptions,
    clearSelection,
    handleBulkUpgrade,
    confirmUpgrade,
    setIsPreviewModalOpen,
    setError: setBulkError,
  } = useSubscriptionBulkUpgrade();

  useEffect(() => {
    let isMounted = true;

    const loadBusinessName = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const businessInfo = await fetchBusinessInfo(user.id);
        if (isMounted) {
          setBusinessName(businessInfo?.business_name || undefined);
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('anchor_billing_enabled')
          .eq('id', user.id)
          .single();
        if (isMounted) {
          setAnchorBillingEnabled(Boolean(profile?.anchor_billing_enabled));
        }
      } catch (error) {
        console.error('[SubscriptionsTab] Failed to load business name:', error);
      }
    };

    loadBusinessName();
    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize and clean up selections when subscriptions change
  useEffect(() => {
    if (subscriptions.length > 0) {
      const newSelected = new Set(selectedSubscriptions);
      let changed = false;
      
      for (const id of Array.from(newSelected)) {
        const sub = subscriptions.find(s => s.id === id);
        if (sub && !canSelectSubscriptionForUpgrade(sub.upgrade_status, sub.has_pending_upgrade)) {
          newSelected.delete(id);
          changed = true;
        }
      }
      
      if (changed) {
        setSelectedSubscriptions(newSelected);
        clearSelection();
        Array.from(newSelected).forEach(id => {
          const sub = subscriptions.find(s => s.id === id);
          if (sub) toggleSubscriptionSelection(id, sub);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions, selectedSubscriptions]);

  // Clear selections when switching tabs (only keep selections in 'regular' tab)
  useEffect(() => {
    if (activeCategory !== 'regular') {
      setSelectedSubscriptions(new Set());
      clearSelection();
    }
  }, [activeCategory, clearSelection]);

  const handleSelectSubscription = (subscription: DashboardSubscription, checked: boolean) => {
    // Only allow selection in the 'regular' tab
    if (activeCategory !== 'regular') {
      return; // Silently ignore selection attempts in other tabs
    }
    
    const canSelect = canSelectSubscriptionForUpgrade(subscription.upgrade_status, subscription.has_pending_upgrade);
    
    if (!canSelect && checked) {
      toast({
        title: "Cannot select this subscription",
        description: "This subscription has already been processed and cannot be selected for upgrade.",
        variant: "destructive",
      });
      return;
    }
    
    const newSelected = new Set(selectedSubscriptions);
    if (checked) {
      newSelected.add(subscription.id);
    } else {
      newSelected.delete(subscription.id);
    }
    setSelectedSubscriptions(newSelected);

    toggleSubscriptionSelection(subscription.id, subscription);
  };
  
  const handleSelectAll = (checked: boolean) => {
    // Only allow select all in the 'regular' tab
    if (activeCategory !== 'regular') {
      return; // Silently ignore select all attempts in other tabs
    }
    
    if (checked) {
      const selectableSubscriptions = regularSubscriptions.filter(sub => 
        canSelectSubscriptionForUpgrade(sub.upgrade_status, sub.has_pending_upgrade)
      );
      
      setSelectedSubscriptions(new Set(selectableSubscriptions.map(sub => sub.id)));
      selectAllSubscriptions(selectableSubscriptions);
    } else {
      setSelectedSubscriptions(new Set());
      clearSelection();
    }
  };

  // Handle bulk upgrade
  const handleBulkUpgradeClick = async () => {
    if (bulkSelectedSubscriptions.size === 0) {
      toast({
        title: "No subscriptions selected",
        description: "Please select at least one subscription to upgrade.",
        variant: "destructive",
      });
      return;
    }

    try {
      setBulkError(null);
      // Pass the raw Stripe data for amount extraction
      await handleBulkUpgrade(mappedSubscriptions, rawStripeData);
    } catch (error: any) {
      toast({
        title: "Bulk upgrade failed",
        description: error.message || "Failed to prepare bulk upgrade",
        variant: "destructive",
      });
    }
  };

  // Handle bulk upgrade confirmation
  const handleConfirmUpgrade = async (selectedTemplate: string) => {
    try {
      await confirmUpgrade(selectedTemplate);
      // Refresh subscription data to show updated status
      await subscriptions.forEach(sub => {
        if (selectedSubscriptions.has(sub.id)) {
          sub.has_pending_upgrade = true;
          sub.upgrade_status = 'email_sent';
        }
      });
      
      setSelectedSubscriptions(new Set());
      clearSelection();
      
      toast({
        title: "Upgrade emails sent",
        description: "Subscription upgrade emails have been sent successfully.",
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Email sending failed",
        description: error.message || "Failed to send upgrade emails",
        variant: "destructive",
      });
    }
  };

  // Handle actions
  const handleView = (subscription: DashboardSubscription) => {
    // Pass the full subscription data to avoid additional queries
    openModal('subscription', subscription.id, subscription);
  };
  
  // Map the subscriptions to add product_id alias for compatibility
  const mappedSubscriptions: DashboardSubscription[] = subscriptions.map(sub => ({
    ...sub,
    product_id: sub.stripe_price_id || '',
    customer_id: sub.id.split('_')[1] || sub.id.substring(0, 8) // Extract customer ID from subscription ID or use fallback
  }));
  
  // Categorize subscriptions into three sections
  const pendingSubscriptions = mappedSubscriptions.filter(sub => {
    return sub.has_pending_upgrade && ['pending', 'email_sent', 'scheduled_for_upgrade'].includes(sub.upgrade_status || '');
  });
  
  const updatedPaymentMethodSubscriptions = mappedSubscriptions.filter(sub => {
    // Subscription with updated payment method
    return sub.has_pending_upgrade && 
           sub.upgrade_status === 'payment_method_updated' || 
           (sub.metadata && sub.metadata.updateMethodPaymentByKeepMore === 'true');
  });
  
  const upgradedPriceSubscriptions = mappedSubscriptions.filter(sub => {
    // Subscription with upgraded price - ONLY check Supabase data
    return sub.has_pending_upgrade && 
           sub.upgrade_status === 'upgraded' && 
           sub.new_price_id; // new_price_id comes from Supabase upgrade record
  });
  
  // Regular subscriptions (not in any of the above categories)
  const regularSubscriptions = mappedSubscriptions.filter(sub => {
    return !pendingSubscriptions.includes(sub) && 
           !updatedPaymentMethodSubscriptions.includes(sub) && 
           !upgradedPriceSubscriptions.includes(sub);
  });

  // Apply status filter to all subscription categories
  const applyStatusFilter = (subs: DashboardSubscription[]) => {
    if (localStatusFilter === 'all') return subs;
    return subs.filter(sub => sub.status === localStatusFilter);
  };

  // Apply status filter to each category
  const filteredRegularSubscriptions = applyStatusFilter(regularSubscriptions);
  const filteredPendingSubscriptions = applyStatusFilter(pendingSubscriptions);
  const filteredUpdatedPaymentSubscriptions = applyStatusFilter(updatedPaymentMethodSubscriptions);
  const filteredUpgradedPriceSubscriptions = applyStatusFilter(upgradedPriceSubscriptions);
  
  // Pagination for each category (using filtered data)
  const regularPagination = usePagination<DashboardSubscription>({ 
    data: filteredRegularSubscriptions, 
    itemsPerPage: 10 
  });
  
  const pendingPagination = usePagination<DashboardSubscription>({ 
    data: filteredPendingSubscriptions, 
    itemsPerPage: 10 
  });
  
  const updatedPaymentPagination = usePagination<DashboardSubscription>({ 
    data: filteredUpdatedPaymentSubscriptions, 
    itemsPerPage: 10 
  });
  
  const upgradedPricePagination = usePagination<DashboardSubscription>({ 
    data: filteredUpgradedPriceSubscriptions, 
    itemsPerPage: 10 
  });
  
  // Helper function to format billing interval
  const formatBillingInterval = (interval?: string) => {
    if (!interval) return 'N/A';
    const intervalMap: Record<string, string> = {
      'day': 'Daily',
      'week': 'Weekly',
      'month': 'Monthly',
      'year': 'Yearly',
    };
    return intervalMap[interval.toLowerCase()] || interval;
  };
  
  // Render custom header with Select All checkbox
  const renderCustomHeader = () => {
    const selectableCount = regularSubscriptions.filter(sub => 
      canSelectSubscriptionForUpgrade(sub.upgrade_status, sub.has_pending_upgrade)
    ).length;
    
    const isAllSelected = selectableCount > 0 && selectedSubscriptions.size === selectableCount;
    const isIndeterminate = selectedSubscriptions.size > 0 && selectedSubscriptions.size < selectableCount;
    
    return (
      <TableHeader>
        <TableRow>
          <TableHead className="px-4 py-3 text-xs uppercase font-medium">
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={handleSelectAll}
                disabled={activeCategory !== 'regular' || selectableCount === 0}
                aria-label="Select all subscriptions"
                className="h-5 w-5 border-2 border-primary/50 rounded-sm"
                data-state={isIndeterminate ? 'indeterminate' : isAllSelected ? 'checked' : 'unchecked'}
              />
            </div>
          </TableHead>
          {columns.slice(1).map((column) => (
            <TableHead
              key={column.id}
              className={`px-4 py-3 text-xs uppercase font-medium ${
                column.align === 'center' ? 'text-center' : 
                column.align === 'right' ? 'text-right' : ''
              }`}
            >
              {column.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
    );
  };
  
  // Render a subscription row
  const renderSubscriptionRow = (sub: DashboardSubscription) => {
    const isSelected = selectedSubscriptions.has(sub.id);
    const isBulkSelected = bulkSelectedSubscriptions.has(sub.id);
    
    return (
      <DataTable.Row key={sub.id} onClick={() => handleView(sub)}>
        {/* Checkbox */}
        <DataTable.Cell onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => handleSelectSubscription(sub, !!checked)}
            disabled={
              activeCategory !== 'regular' || 
              !canSelectSubscriptionForUpgrade(sub.upgrade_status, sub.has_pending_upgrade)
            }
            aria-label={`Select subscription ${sub.id}`}
            className="h-5 w-5 border-2 border-primary/50 rounded-sm"
          />
        </DataTable.Cell>
        
        {/* Customer Name and Email */}
        <DataTable.Cell className="min-w-[180px]">
          <div className="space-y-0.5">
            <div className="font-medium text-sm">
              {sub.customer_name || sub.customer_email?.split('@')[0] || 'Unknown Customer'}
            </div>
            {sub.customer_email && (
              <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                {sub.customer_email}
              </div>
            )}
          </div>
        </DataTable.Cell>
        
        {/* Product/Subscription Name */}
        <DataTable.Cell className="min-w-[180px]">
          <div className="font-medium text-sm truncate">
            {sub.product_name || 'Unknown Product'}
          </div>
        </DataTable.Cell>
        
        {/* Amount / New Total — amount already contains the updated price from Stripe */}
        <DataTable.Cell className="whitespace-nowrap text-sm">
          {sub.amount ? `$${(sub.amount / 100).toFixed(2)}` : '—'}
        </DataTable.Cell>

        {/* Fees — only in eligible & pending tabs */}
        {(activeCategory === 'regular' || activeCategory === 'pending') && (
          <DataTable.Cell className="whitespace-nowrap text-sm">
            {sub.amount ? `$${((sub.amount / 100) * STRIPE_PERCENT_FEE + STRIPE_FIXED_FEE).toFixed(2)}` : '—'}
          </DataTable.Cell>
        )}

        {/* Savings — only in switched to ACH & updated price tabs */}
        {(activeCategory === 'updated' || activeCategory === 'upgraded') && (
          <DataTable.Cell className="whitespace-nowrap text-sm">
            {sub.amount ? `$${((sub.amount / 100) * STRIPE_PERCENT_FEE + STRIPE_FIXED_FEE).toFixed(2)}` : '—'}
          </DataTable.Cell>
        )}

        <DataTable.Cell className="whitespace-nowrap text-sm">
          {formatBillingInterval(sub.billing_interval)}
        </DataTable.Cell>

        <DataTable.Cell className="whitespace-nowrap text-sm">
          {(() => {
            if (activeCategory === 'regular') {
              return sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—';
            }
            // For pending/updated/upgraded, show payment_method_updated_at or grace_period_end
            const updatedDate = sub.payment_method_updated_at || sub.grace_period_end;
            return updatedDate ? new Date(updatedDate).toLocaleDateString() : '—';
          })()}
        </DataTable.Cell>
        
      </DataTable.Row>
    );
  };

  return (
    <>
      {/* Migration Info Header - Outside the table */}
      <div className="mb-6">
        <SubscriptionMigrationInfo />
      </div>
      <SubscriptionSavingsMetrics
        totalSaved={savingsMetrics.totalSaved}
        remainingEligible={savingsMetrics.remainingEligible}
        migratedCount={savingsMetrics.migratedCount}
        eligibleCount={savingsMetrics.eligibleCount}
      />
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-foreground">Select Subscriptions to Update</h2>
        <RefreshButton
          onClick={() => refreshSubscriptions()}
          isRefreshing={loading}
        />
      </div>

      <Card className="border shadow-sm rounded-2xl">
        <CardContent className="p-0">
           {isLoading || loading ? (
            <div className="text-center py-12 bg-card">
              <div className="space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="text-muted-foreground">Loading subscriptions...</p>
                <p className="text-xs text-gray-500">
                  {loading}
                </p>
              </div>
            </div>
          ) : !stripeConnected ? (
            <EmptyState 
              title="Connect your Stripe account"
              description="Connect your Stripe account to access KeepMore subscription details and manage your plans."
              actionLabel="Connect Stripe Account"
              identifier={true}
              icon={<ArrowUpRight className="h-12 w-12" />}
             />
           ) : (
            <>
            {/* Selection bar - always visible */}
            <div className="flex items-center justify-between p-4 border-b bg-muted/20">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground">
                  {selectedSubscriptions.size} subscription{selectedSubscriptions.size !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setSelectedSubscriptions(new Set());
                    clearSelection();
                  }}
                  disabled={selectedSubscriptions.size === 0}
                  className="text-sm"
                >
                  Clear selection
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={handleBulkUpgradeClick}
                  disabled={isBulkLoading || selectedSubscriptions.size === 0}
                  className="gap-2 bg-blue-500 hover:bg-blue-600"
                >
                  <TrendingUp className="h-4 w-4" />
                  {isBulkLoading ? 'Sending...' : 'Send Update Notice'}
                </Button>
              </div>
            </div>
            
            <div className="overflow-x-auto">  
              {/* Horizontal Tabs for Subscription Categories */}
              <div className="px-4 pt-2">
                <div className="flex space-x-6 overflow-x-auto border-b">
                  <button 
                    className={`px-1 pb-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeCategory === 'regular' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                    }`}
                    onClick={() => setActiveCategory('regular')}
                  >
                    Eligible Subscriptions ({filteredRegularSubscriptions.length})
                  </button>
                  <button 
                    className={`px-1 pb-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeCategory === 'pending' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                    }`}
                    onClick={() => setActiveCategory('pending')}
                  >
                    Pending/Processing ({filteredPendingSubscriptions.length})
                  </button>
                  <button 
                    className={`px-1 pb-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeCategory === 'updated' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                    }`}
                    onClick={() => setActiveCategory('updated')}
                  >
                    Switched to ACH ({filteredUpdatedPaymentSubscriptions.length})
                  </button>
                  <button 
                    className={`px-1 pb-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeCategory === 'upgraded' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                    }`}
                    onClick={() => setActiveCategory('upgraded')}
                  >
                    Updated Price ({filteredUpgradedPriceSubscriptions.length})
                  </button>
                </div>
              </div>
              
              {/* Display the currently active category */}
              <div className='pb-20'>
                {activeCategory === 'regular' && filteredRegularSubscriptions.length > 0 && (
                  <>
                    <DataTable>
                      {renderCustomHeader()}
                      <TableBody>
                        {regularPagination.paginatedData.map(sub => renderSubscriptionRow(sub))}
                      </TableBody>
                    </DataTable>
                    {filteredRegularSubscriptions.length > 10 && (
                      <div className="flex items-center justify-between p-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          Showing {regularPagination.startItem} to {regularPagination.endItem} of {regularPagination.totalItems} items
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={regularPagination.goToPreviousPage} 
                            disabled={!regularPagination.hasPreviousPage}
                          >
                            Previous
                          </Button>
                          <span className="text-sm">
                            Page {regularPagination.currentPage} of {regularPagination.totalPages}
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={regularPagination.goToNextPage} 
                            disabled={!regularPagination.hasNextPage}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {activeCategory === 'pending' && filteredPendingSubscriptions.length > 0 && (
                  <>
                    <DataTable>
                      {renderCustomHeader()}
                      <TableBody>
                        {pendingPagination.paginatedData.map(sub => renderSubscriptionRow(sub))}
                      </TableBody>
                    </DataTable>
                    {filteredPendingSubscriptions.length > 10 && (
                      <div className="flex items-center justify-between p-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          Showing {pendingPagination.startItem} to {pendingPagination.endItem} of {pendingPagination.totalItems} items
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={pendingPagination.goToPreviousPage} 
                            disabled={!pendingPagination.hasPreviousPage}
                          >
                            Previous
                          </Button>
                          <span className="text-sm">
                            Page {pendingPagination.currentPage} of {pendingPagination.totalPages}
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={pendingPagination.goToNextPage} 
                            disabled={!pendingPagination.hasNextPage}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {activeCategory === 'updated' && filteredUpdatedPaymentSubscriptions.length > 0 && (
                  <>
                    <DataTable>
                      {renderCustomHeader()}
                      <TableBody>
                        {updatedPaymentPagination.paginatedData.map(sub => renderSubscriptionRow(sub))}
                      </TableBody>
                    </DataTable>
                    {filteredUpdatedPaymentSubscriptions.length > 10 && (
                      <div className="flex items-center justify-between p-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          Showing {updatedPaymentPagination.startItem} to {updatedPaymentPagination.endItem} of {updatedPaymentPagination.totalItems} items
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={updatedPaymentPagination.goToPreviousPage} 
                            disabled={!updatedPaymentPagination.hasPreviousPage}
                          >
                            Previous
                          </Button>
                          <span className="text-sm">
                            Page {updatedPaymentPagination.currentPage} of {updatedPaymentPagination.totalPages}
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={updatedPaymentPagination.goToNextPage} 
                            disabled={!updatedPaymentPagination.hasNextPage}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {activeCategory === 'upgraded' && filteredUpgradedPriceSubscriptions.length > 0 && (
                  <>
                    <DataTable>
                      {renderCustomHeader()}
                      <TableBody>
                        {upgradedPricePagination.paginatedData.map(sub => renderSubscriptionRow(sub))}
                      </TableBody>
                    </DataTable>
                    {filteredUpgradedPriceSubscriptions.length > 10 && (
                      <div className="flex items-center justify-between p-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          Showing {upgradedPricePagination.startItem} to {upgradedPricePagination.endItem} of {upgradedPricePagination.totalItems} items
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={upgradedPricePagination.goToPreviousPage} 
                            disabled={!upgradedPricePagination.hasPreviousPage}
                          >
                            Previous
                          </Button>
                          <span className="text-sm">
                            Page {upgradedPricePagination.currentPage} of {upgradedPricePagination.totalPages}
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={upgradedPricePagination.goToNextPage} 
                            disabled={!upgradedPricePagination.hasNextPage}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Empty state for the active category */}
                {((activeCategory === 'regular' && regularSubscriptions.length === 0) ||
                  (activeCategory === 'pending' && pendingSubscriptions.length === 0) ||
                  (activeCategory === 'updated' && updatedPaymentMethodSubscriptions.length === 0) ||
                  (activeCategory === 'upgraded' && upgradedPriceSubscriptions.length === 0)) && (
                  <div className="p-8 text-center">
                    <p className="text-muted-foreground">No subscriptions found in this category</p>
                  </div>
                )}
              </div>
              
              {/* Loading, error, or empty state */}
              {loading && <DataTable.Loading colSpan={columns.length} />}
              {error && <DataTable.Empty colSpan={columns.length} message={error} />}
            </div>
          </>
        )}
      </CardContent>
      
        {/* Bulk Upgrade Preview Modal */}
        <BulkUpgradePreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          onConfirm={handleConfirmUpgrade}
          preview={preview}
          isLoading={isBulkLoading}
          businessName={businessName}
          anchorBillingEnabled={anchorBillingEnabled}
        />
      </Card>
    </>
  );
};

export default SubscriptionsTab;